import { useNavigate, useParams } from '@tanstack/react-router';
import { Button, toast } from '@devhop/ui';
import { ArrowLeft, HelpCircle } from 'lucide-react';
import { Header } from '@/app/layout/Header';
import { Main } from '@/app/layout/Main';
import {
    usePromotionRuleDetailQuery,
    useUpdatePromotionRuleMutation,
} from '../api/queries';
import { PromotionRuleForm } from '../ui/PromotionRuleForm';
import {
    PromotionTour,
    promotionRuleEditTourSteps,
    usePromotionTour,
} from '../tour';

export function PromotionRuleEditPage() {
    const nav = useNavigate();
    const { id } = useParams({ from: '/app/hr/promotion/rules/$id/edit' });
    const detailQuery = usePromotionRuleDetailQuery(id);
    const updateMutation = useUpdatePromotionRuleMutation();
    const tour = usePromotionTour('ruleEdit');

    const back = () =>
        nav({
            to: '/app/hr/promotion/rules',
            search: {
                offset: 0,
                limit: 20,
                sort: undefined,
                isActive: undefined,
                fromPositionId: undefined,
                toPositionId: undefined,
                search: undefined,
            },
        });

    if (detailQuery.isLoading || !detailQuery.data) {
        return (
            <>
                <Header />
                <Main>
                    <div className="text-muted-foreground">
                        {detailQuery.isLoading
                            ? 'ກຳລັງໂຫຼດ...'
                            : 'ບໍ່ພົບຂໍ້ກຳນົດ'}
                    </div>
                </Main>
            </>
        );
    }

    return (
        <>
            <Header />
            <Main>
                <PromotionTour
                    steps={promotionRuleEditTourSteps}
                    run={tour.run}
                    onCallback={tour.handleJoyrideCallback}
                />
                <div
                    className="mb-6 flex items-center justify-between gap-2"
                    data-tourid="promotion-rule-edit-back"
                >
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="font-semibold text-2xl">
                                ແກ້ໄຂຂໍ້ກຳນົດການເລື່ອນຕຳແໜ່ງ
                            </h1>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="size-7 rounded-full"
                                onClick={tour.startTour}
                                title="ເລີ່ມທົດສອບການນຳທາງ"
                                aria-label="ເລີ່ມທົດສອບການນຳທາງ"
                            >
                                <HelpCircle className="size-5" />
                            </Button>
                        </div>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={back}
                        className="gap-2"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        ກັບໄປລາຍການ
                    </Button>
                </div>
                <div
                    className="mx-auto max-w-3xl"
                    data-tourid="promotion-rule-edit-form"
                >
                    <PromotionRuleForm
                    mode="edit"
                    rule={detailQuery.data}
                    onCancel={back}
                    isSubmitting={updateMutation.isPending}
                    onSubmit={(values) => {
                        updateMutation.mutate(
                            { id, dto: values },
                            {
                                onSuccess: () => {
                                    toast.success('ບັນທຶກການແກ້ໄຂແລ້ວ');
                                    back();
                                },
                                onError: (err) =>
                                    toast.error(
                                        err.message ?? 'ບັນທຶກບໍ່ສຳເລັດ',
                                    ),
                            },
                        );
                    }}
                    />
                </div>
            </Main>
        </>
    );
}
